import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Button,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { getSchemeIcon, getBookLabel } from '@/lib/schemeIcons';
import api from '@/lib/api';
import MainLayout from '@/components/MainLayout';
import { sameDay, formatTimeShort, durationMinutes, addDays, startOfDay } from '@/components/calendar/dateUtils';
import { toast } from '@/lib/toast';

const firstName = (name) => (name || '').trim().split(/\s+/)[0] || 'there';

const greetingFor = (d = new Date()) => {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const STATUS_LABEL = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

// Timeline rail color per status (mirrors StatusDot's semantics).
const railColor = (t, status) => {
  if (status === 'completed') return t.palette.success.main;
  if (status === 'in_progress') return t.palette.primary.main;
  if (status === 'no_show') return t.palette.error.main;
  if (status === 'cancelled') return t.palette.action.disabled;
  return t.palette.divider;
};

/* ---------------------------------------------------------------- KPI band */

function KpiCell({ label, value, sub, to, highlight, last }) {
  const navigate = useNavigate();
  return (
    <Box
      onClick={to ? () => navigate(to) : undefined}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      sx={{
        p: { xs: 2.5, lg: 3.5 },
        cursor: to ? 'pointer' : 'default',
        borderRight: { lg: last ? 0 : 1 },
        borderBottom: { xs: 1, lg: 0 },
        borderColor: { xs: 'divider', lg: 'divider' },
        // The revenue cell wears the brand accent as data — same fill/ink
        // pairing as primary buttons, so it works per-tenant in both modes.
        ...(highlight && {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }),
        '&:hover': to && !highlight ? { bgcolor: 'action.hover' } : undefined,
        transition: 'background-color 120ms ease',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1.25,
          fontWeight: highlight ? 600 : 500,
          color: highlight ? 'inherit' : 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: 32, lg: 40 }, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: highlight ? 'inherit' : 'text.secondary', fontWeight: highlight ? 600 : 400 }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------- Today card */

function TodayTimeline({ appointments, currentUserId, isAdmin }) {
  const navigate = useNavigate();
  const now = new Date();

  const todays = useMemo(
    () =>
      appointments
        .filter((a) => sameDay(a.start_time, now))
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointments],
  );

  const mine = useMemo(
    () => todays.filter((a) => a.trainer_id === currentUserId),
    [todays, currentUserId],
  );

  // Same rule as before: show the user's own day; admins with nothing
  // assigned see the whole team's day.
  const showingAll = isAdmin && mine.length === 0;
  const rows = showingAll ? todays : mine;

  // Insert the "now" marker before the first appointment that starts later.
  const nowIndex = rows.findIndex((a) => new Date(a.start_time) > now);
  const markerAt = rows.length === 0 ? -1 : nowIndex === -1 ? rows.length : nowIndex;

  return (
    <Paper variant="outlined" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: 17 }}>Today</Typography>
        <Typography variant="caption" color="text.secondary">
          {showingAll ? 'All staff' : 'Assigned to you'} · {rows.length} session{rows.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {rows.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
          No appointments today.
        </Typography>
      ) : (
        <Box sx={{ overflowY: 'auto', maxHeight: 400, flex: 1 }}>
          {rows.map((appt, i) => {
            const past = new Date(appt.end_time || appt.start_time) < now;
            return (
              <React.Fragment key={appt.appointment_id}>
                {i === markerAt && (
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ my: 0.25 }}>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        flexShrink: 0,
                      }}
                    >
                      NOW {formatTimeShort(now)}
                    </Typography>
                    <Box sx={{ flex: 1, height: 2, bgcolor: 'primary.main', borderRadius: 1 }} />
                  </Stack>
                )}
                <Stack
                  direction="row"
                  spacing={2}
                  onClick={() => navigate('/calendar')}
                  sx={{
                    py: 1.25,
                    px: 0.5,
                    cursor: 'pointer',
                    opacity: past ? 0.55 : 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: 64, textAlign: 'right', flexShrink: 0, pt: 0.25, fontWeight: 500 }}
                  >
                    {formatTimeShort(appt.start_time)}
                  </Typography>
                  <Box sx={{ width: 2, borderRadius: 1, bgcolor: (t) => railColor(t, appt.status), flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {appt.service_name || 'Appointment'}
                      {appt.member_name ? ` — ${appt.member_name}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {STATUS_LABEL[appt.status] || appt.status} · {durationMinutes(appt)} min
                    </Typography>
                  </Box>
                </Stack>
              </React.Fragment>
            );
          })}
          {markerAt === rows.length && rows.length > 0 && null}
        </Box>
      )}
      <Box sx={{ mt: 'auto', pt: 1.5 }}>
        <Button component={RouterLink} to="/calendar" size="small" sx={{ fontWeight: 600 }}>
          Open calendar →
        </Button>
      </Box>
    </Paper>
  );
}

/* ------------------------------------------------------- Needs attention */

function NeedsAttention({ items }) {
  if (items.length === 0) return null;
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontSize: 17, mb: 1.5 }}>
        Needs attention
      </Typography>
      <Stack spacing={1.25}>
        {items.map((item) => (
          <Stack key={item.key} direction="row" spacing={1.25} alignItems="baseline">
            <Typography variant="caption" fontWeight={600} sx={{ color: item.color, flexShrink: 0 }}>
              {item.count}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              {item.label}
            </Typography>
            <Button component={RouterLink} to={item.to} size="small" sx={{ fontWeight: 600, flexShrink: 0 }}>
              View →
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

/* -------------------------------------------------------------- Activity */

const ACTIVITY_SENTENCE = {
  appointment_created: 'created an appointment',
  appointment_updated: 'updated an appointment',
  appointment_deleted: 'deleted an appointment',
  member_created: 'added a member',
  member_updated: 'updated a member',
  service_updated: 'updated a service',
  branding_updated: 'updated branding',
  lead_fields_updated: 'updated the lead form',
  site_updated: 'updated location settings',
  user_registered: 'registered',
};

const HIGHLIGHT_ACTIONS = new Set(['appointment_created', 'member_created', 'user_registered']);

const prettifyAction = (action) => (action || '').replace(/_/g, ' ');

const timeAgo = (iso) => {
  if (!iso) return '';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
};

function RecentActivity({ entries }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ fontSize: 17 }} gutterBottom>
        Activity
      </Typography>
      {entries.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
          No activity yet.
        </Typography>
      ) : (
        <Box sx={{ overflowY: 'auto', maxHeight: 372 }}>
          {entries.map((e) => {
            const sentence = ACTIVITY_SENTENCE[e.action] || prettifyAction(e.action);
            const highlight = HIGHLIGHT_ACTIONS.has(e.action);
            return (
              <Stack
                key={e.audit_id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: highlight ? 'primary.main' : 'action.disabled',
                  }}
                />
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  <Box component="span" fontWeight={600}>
                    {e.performer_name || 'Someone'}
                  </Box>{' '}
                  {sentence}
                  {e.detail ? ` · ${e.detail.replace(/_/g, ' ')}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {timeAgo(e.created_at)}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

/* ------------------------------------------------------------------ Page */

const DashboardPage = () => {
  const { user, isMember, isAdmin } = useAuth();
  const { branding } = useBranding();
  const SchemeIcon = getSchemeIcon(branding?.scheme);
  const bookLabel = getBookLabel(branding?.scheme);
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isMember) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const apptReq = api.get('/appointments');
        const kpiReq = isAdmin ? api.get('/dashboard/kpis') : null;
        const actReq = isAdmin ? api.get('/dashboard/activity') : null;
        const invReq = isAdmin ? api.get('/invoices') : null;
        const [apptRes, kpiRes, actRes, invRes] = await Promise.all([apptReq, kpiReq, actReq, invReq]);
        setAppointments(Array.isArray(apptRes.data) ? apptRes.data : []);
        if (kpiRes) setKpis(kpiRes.data);
        if (actRes) setActivity(Array.isArray(actRes.data) ? actRes.data : []);
        if (invRes) setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
      } catch {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isMember, isAdmin]);

  const inProgressCount = useMemo(
    () => appointments.filter((a) => sameDay(a.start_time, new Date()) && a.status === 'in_progress').length,
    [appointments],
  );

  // "Needs attention" derives from data the app already loads — no new
  // endpoints. Rows with a zero count are hidden; the card hides entirely
  // when everything is clear.
  const attentionItems = useMemo(() => {
    const items = [];
    const pendingInvoices = invoices.filter((i) => i.status === 'pending').length;
    if (pendingInvoices > 0) {
      items.push({
        key: 'invoices',
        count: pendingInvoices,
        label: `invoice${pendingInvoices === 1 ? '' : 's'} awaiting payment`,
        color: 'error.main',
        to: '/billing',
      });
    }
    const yesterday = startOfDay(addDays(new Date(), -1));
    const noShows = appointments.filter((a) => sameDay(a.start_time, yesterday) && a.status === 'no_show').length;
    if (noShows > 0) {
      items.push({
        key: 'no-shows',
        count: noShows,
        label: `no-show${noShows === 1 ? '' : 's'} yesterday to follow up`,
        color: 'text.secondary',
        to: '/calendar',
      });
    }
    return items;
  }, [invoices, appointments]);

  if (isMember) {
    return (
      <MainLayout>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography variant="h4" gutterBottom>
            {greetingFor()}, {firstName(user?.name)}
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <CalendarTodayIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Your schedule
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                  View and manage your upcoming sessions
                </Typography>
                <Button component={RouterLink} to="/schedule" color="primary" variant="text" sx={{ fontWeight: 600 }}>
                  View schedule →
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <SchemeIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {bookLabel}
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                  Schedule your next visit
                </Typography>
                <Button component={RouterLink} to="/book" color="secondary" variant="text" sx={{ fontWeight: 600 }}>
                  Book now →
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'flex-end' }}
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </Typography>
            <Typography variant="h4" sx={{ letterSpacing: '-0.01em' }}>
              {greetingFor()}, {firstName(user?.name)}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/calendar?new=true')}
            data-testid="add-appointment-button"
          >
            Book session
          </Button>
        </Stack>

        {loading ? (
          <>
            <Skeleton variant="rounded" height={130} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} lg={7}>
                <Skeleton variant="rounded" height={360} />
              </Grid>
              <Grid item xs={12} lg={5}>
                <Skeleton variant="rounded" height={360} />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            {isAdmin && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', borderRadius: 4 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', lg: '1fr 1fr 1fr 1.2fr' },
                  }}
                >
                  <KpiCell
                    label="Active members"
                    value={kpis?.active_members ?? 0}
                    to="/members"
                  />
                  <KpiCell
                    label="New leads"
                    value={kpis?.new_leads ?? 0}
                    to="/leads"
                  />
                  <KpiCell
                    label="Today's bookings"
                    value={kpis?.todays_appointments ?? 0}
                    sub={inProgressCount > 0 ? `${inProgressCount} in progress` : null}
                    to="/calendar"
                  />
                  <KpiCell
                    label="Total revenue"
                    value={`$${Math.round(kpis?.total_revenue ?? 0).toLocaleString()}`}
                    to="/billing"
                    highlight
                    last
                  />
                </Box>
              </Paper>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} lg={isAdmin ? 7 : 12}>
                <TodayTimeline
                  appointments={appointments}
                  currentUserId={user?.user_id}
                  isAdmin={isAdmin}
                />
              </Grid>
              {isAdmin && (
                <Grid item xs={12} lg={5}>
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <NeedsAttention items={attentionItems} />
                    <Box sx={{ flex: 1 }}>
                      <RecentActivity entries={activity} />
                    </Box>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Box>
    </MainLayout>
  );
};

export default DashboardPage;
